import { Configure, Hits, useConnector, useSortBy } from 'react-instantsearch-hooks-web';
import connectStats from 'instantsearch.js/es/connectors/stats/connectStats';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import type {
  StatsConnectorParams,
  StatsWidgetDescription,
} from 'instantsearch.js/es/connectors/stats/connectStats';
import { CustomPagination } from '../../CustomPagination';
import classNames from 'classnames';
import NrcSearchHit from 'components/helpers/AlgoliaSearch/NetworkResourceCenter/NRCSearchHit';
import { REPORTS_SEARCH_INDEX } from 'lib/util/algolia';
import { env } from 'lib/env';
import { useEffect, useMemo } from 'react';
import { HitProps, HIT_FIELDS, SnippetType, SnippetTypeObject } from '../../Hits/_types';
import { useSearchDictionary } from '../../util';

const attributesToHighlight: HIT_FIELDS[] = ['title', 'fileName', 'typeOfFile'];

const attributesToSnippet: SnippetType[] = [{ field: 'fileName', maxWords: 40 }];

const mappedSnippets = attributesToSnippet.map((x: HIT_FIELDS | SnippetTypeObject): string => {
  const obj = x as SnippetTypeObject;
  if (obj.field && obj.maxWords) {
    return `${obj.field}:${obj.maxWords}`;
  }
  if (obj.field && obj.maxWords) {
    return obj.field;
  }
  return x as HIT_FIELDS;
});

const filter =
  'NOT reportTypeFacet:"CT Instructions" AND ' +
  'NOT reportTypeFacet: "Fees CO" AND ' +
  'NOT reportTypeFacet: "Fees CT" AND ' +
  'NOT reportTypeFacet: "Cooperative Instructions" AND ' +
  'NOT reportTypeFacet: "Courier"';

export type UseStatsProps = StatsConnectorParams;

function useStats(props?: UseStatsProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useConnector<StatsConnectorParams, StatsWidgetDescription>(connectStats as any, props);
}

const ReportSearchHit = ({ hit }: HitProps) => {
  return <NrcSearchHit hit={hit} component="report" />;
};

const ReportsResultsWithSearch = (props: UseStatsProps) => {
  const dict = useSearchDictionary();
  const { sortNewest, sortNameAsc, sortNameDesc } = dict;

  const items = useMemo(() => {
    const reportsSearchIndex = REPORTS_SEARCH_INDEX();
    return [
      { label: sortNewest, value: `${reportsSearchIndex}_date_desc` },
      { label: sortNameAsc, value: `${reportsSearchIndex}_name_asc` },
      { label: sortNameDesc, value: `${reportsSearchIndex}_name_desc` },
      { label: 'not-shown-default', value: `${env.ALGOLIA_INDEX_NAME}` },
      { label: 'not-shown-date', value: `${env.ALGOLIA_INDEX_NAME}_date_desc` },
    ];
  }, [sortNameAsc, sortNameDesc, sortNewest]);
  const { refine } = useSortBy({ items });
  const isBrowser = typeof window !== 'undefined'; // Check if window is defined
  useEffect(() => {
    if (isBrowser) {
      const delay = 100;
      const timer = setTimeout(() => {
        const reportsIndex = `${REPORTS_SEARCH_INDEX()}_date_desc`;
        refine(reportsIndex);
      }, delay);
      return () => clearTimeout(timer);
    }
    return () => 0;
  }, [refine, isBrowser]);

  const { nbHits } = useStats(props);
  const showPagination = nbHits > 10;
  return (
    <div>
      <Configure
        attributesToHighlight={attributesToHighlight}
        attributesToSnippet={mappedSnippets}
        filters={filter}
        analytics={false}
        hitsPerPage={10}
      />

      <div>
        <Hits hitComponent={ReportSearchHit} />
      </div>
      <div className={classNames({ hidden: !showPagination })}>
        <CustomPagination padding={1} />
      </div>
    </div>
  );
};

const ReportsResults = (props: UseStatsProps) => {
  const { sitecoreContext } = useSitecoreContext();
  const pageEditing = sitecoreContext.pageEditing === true;
  if (pageEditing) {
    return <div>Search Results will be shown here</div>;
  }
  return <ReportsResultsWithSearch {...props} />;
};

export default ReportsResults;
